import React, { useState, useEffect, useContext } from 'react';
import { InspectionItemsClient, CategorysClient } from '../web-api-client.ts';
import { Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt, faTrash } from '@fortawesome/free-solid-svg-icons';
import BackButton from './BackButton.js';
import Loader from './Loader.js';
import UserContext from '../providers/UserContext.js';

export function InspectionItems() {
  const [inspectionitems, setInspectionItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingInspectionItem, setEditingInspectionItem] = useState(null);

  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const user = useContext(UserContext);

  // Keep categoryId empty string until user selects one (better UX than defaulting to 0)
  const [newInspectionItem, setNewInspectionItem] = useState({
    name: '',
    categoryId: '',
    hasPhoto: false,
    description: '',
    statusLabelGreen: 'Good',
    statusLabelYellow: 'Fair',
    statusLabelRed: 'Poor',
    sortOrder: '',

  });

  useEffect(() => {
    populateInspectionItemData();
    populateCategories();
  }, []);

  async function populateInspectionItemData() {
    const client = new InspectionItemsClient();
    const data = await client.getInspectionItemList(user.dealerId);
    setInspectionItems(data);
    setLoading(false);
  }

  async function populateCategories() {
    const catClient = new CategorysClient();
    const list = await catClient.getCategoryList(user.dealerId); 
    // Expecting [{ id, name }, ...]
    setCategories(list || []);
    setCategoriesLoading(false);
  }

  function handleEditClick(inspectionitem) {
    setEditingInspectionItem({
      id: inspectionitem.id ?? 0,
      categoryId: inspectionitem.categoryId ?? '',
      name: inspectionitem.name ?? '',
      hasPhoto: Boolean(inspectionitem.hasPhoto),
      description: inspectionitem.description ?? '',
      statusLabelGreen: inspectionitem.statusLabelGreen ?? 'Good',
      statusLabelYellow: inspectionitem.statusLabelYellow ?? 'Fair',
      statusLabelRed: inspectionitem.statusLabelRed ?? 'Poor',
      sortOrder: inspectionitem.sortOrder ?? '',
    });
    setShowModal(true);
  }

  async function handleDeleteClick(inspectionitemToDelete) {
    const client = new InspectionItemsClient();
    await client.deleteInspectionItem(inspectionitemToDelete.id);
    setInspectionItems(inspectionitems.filter(p => p.id !== inspectionitemToDelete.id));
  }

  async function handleModalSubmit() {
    if (!editingInspectionItem) return;
    const client = new InspectionItemsClient();
    const payload = normalizeDto(editingInspectionItem);
    const updated = await client.updateInspectionItem(payload.id, payload);
    setInspectionItems(items => items.map(i => (i.id === updated.id ? updated : i)));
    setShowModal(false);
  }

  async function handleFormSubmit(event) {
    event.preventDefault();
    const client = new InspectionItemsClient();
    const payload = normalizeDto(newInspectionItem);
    const created = await client.createInspectionItem(payload);
    setInspectionItems([...inspectionitems, created]);
    setNewInspectionItem({
      name: '',
      categoryId: '',
      hasPhoto: false,
      description: '',
      statusLabelGreen: 'Good',
      statusLabelYellow: 'Fair',
      statusLabelRed: 'Poor',
      sortOrder: '',
    });
  }

  // Ensure types match the DTO the server expects
  function normalizeDto(dto) {
    return {
      id: dto.id ?? 0,
      categoryId:
        dto.categoryId === '' || dto.categoryId === null
          ? 0
          : Number.isFinite(Number(dto.categoryId))
            ? Number(dto.categoryId)
            : 0,
      name: dto.name ?? '',
      hasPhoto: Boolean(dto.hasPhoto),
      description: dto.description ?? ''
    };
  }

  // New form input handler
  function handleNewChange(e) {
    const { name, type, value, checked, valueAsNumber } = e.target;
    if (name === 'categoryId') {
      setNewInspectionItem(prev => ({ ...prev, categoryId: value === '' ? '' : Number(value) }));
      return;
    }
    setNewInspectionItem(prev => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? checked
          : type === 'number'
            ? (Number.isNaN(valueAsNumber) ? '' : valueAsNumber)
            : value
    }));
  }

  // Edit modal input handler
  function handleEditChange(e) {
    const { name, type, value, checked, valueAsNumber } = e.target;
    if (name === 'categoryId') {
      setEditingInspectionItem(prev => ({ ...prev, categoryId: value === '' ? '' : Number(value) }));
      return;
    }
    setEditingInspectionItem(prev => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? checked
          : type === 'number'
            ? (Number.isNaN(valueAsNumber) ? '' : valueAsNumber)
            : value
    }));
  }

  function getCategoryName(catId) {
    const c = categories.find(c => c.id === catId);
    return c ? c.name : '';
  }

  function renderInspectionItemsTable(inspectionitems) {
    return (
      <div>
        <BackButton />
        <h1 id="tableLabel">Edit Inspection Items</h1>
        <table className="table table-striped" aria-labelledby="tableLabel">
          <thead>
            <tr>
              <th>Name</th>
              <th>Category</th>
              <th>Requires Photos</th>

              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {inspectionitems.map(inspectionitem => (
              <tr key={inspectionitem.id}>
                <td>{inspectionitem.name}</td>
                <td>{getCategoryName(inspectionitem.categoryId) || inspectionitem.categoryId}</td>
                <td>{inspectionitem.hasPhoto ? 'Yes' : 'No'}</td>

                <td>
                  <FontAwesomeIcon
                    icon={faPencilAlt}
                    role="button"
                    onClick={() => handleEditClick(inspectionitem)}
                  />
                  &nbsp;&nbsp;&nbsp;
                  <FontAwesomeIcon
                    icon={faTrash}
                    role="button"
                    onClick={() => handleDeleteClick(inspectionitem)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Edit Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)} backdrop="static">
          <Modal.Header closeButton>
            <Modal.Title>Edit Inspection Item</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {editingInspectionItem && (
              <form onSubmit={e => e.preventDefault()}>
                <div className="form-group mb-2">
                  <label>Name</label>
                  <input
                    name="name"
                    type="text"
                    className="form-control"
                    value={editingInspectionItem.name}
                    onChange={handleEditChange}
                    required
                  />
                </div>

                <div className="form-group mb-2">
                  <label>Category</label>
                  <select
                    name="categoryId"
                    className="form-control"
                    value={
                      editingInspectionItem.categoryId === 0
                        ? ''
                        : editingInspectionItem.categoryId ?? ''
                    }
                    onChange={handleEditChange}
                    required
                    disabled={categoriesLoading}
                  >
                    <option value="" disabled>
                      {categoriesLoading ? 'Loading categories…' : 'Select a category'}
                    </option>
                    {categories.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group form-check mb-2">
                  <input
                    id="edit-hasPhoto"
                    name="hasPhoto"
                    type="checkbox"
                    className="form-check-input"
                    checked={!!editingInspectionItem.hasPhoto}
                    onChange={handleEditChange}
                  />
                  <label className="form-check-label" htmlFor="edit-hasPhoto">
                    Requires Photos
                  </label>
                </div>

                <div className="form-group mb-2">
                  <label>Help Text</label>
                  <textarea
                    name="description"
                    className="form-control"
                    rows={3}
                    value={editingInspectionItem.description ?? ''}
                    onChange={handleEditChange}
                    placeholder="Descriptive text shown to the customer about this inspection item"
                  />
                </div>
                 <div className="form-group mb-2">
          <label>Status Label Green</label>
          <input
            name="statusLabelGreen"
            value={editingInspectionItem.statusLabelGreen}
            onChange={handleEditChange}
            placeholder="Status Label Green"
            required
            className="form-control"
          />
        </div>
        <div className="form-group mb-2">
          <label>Status Label Yellow</label>
          <input
            name="statusLabelYellow"
            value={editingInspectionItem.statusLabelYellow}
            onChange={handleEditChange}
            placeholder="Status Label Yellow"
            required
            className="form-control"
          />
        </div>
        <div className="form-group mb-2">
          <label>Status Label Red</label>
          <input
            name="statusLabelRed"
            value={editingInspectionItem.statusLabelRed}
            onChange={handleEditChange}
            placeholder="Status Label Red"
            required
            className="form-control"
          />
        </div>
        <div className="form-group mb-4">
          <label>Sort Order</label>
          <input
            name="sortOrder"
            value={editingInspectionItem.sortOrder}
            onChange={handleEditChange}
            placeholder="Sort Order"
            required
            className="form-control"
            type='number'
          />
        </div>




              </form>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Close
            </Button>
            <Button variant="primary" onClick={handleModalSubmit}>
              Save Changes
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }

  const contents = loading ? <Loader /> : renderInspectionItemsTable(inspectionitems);

  return (
    <div>
      {contents}

      <h3 className="mt-4">Add Inspection Item</h3>
      <form onSubmit={handleFormSubmit} className="mt-3" style={{ maxWidth: 640 }}>
        <div className="form-group mb-2">
          <label>Name</label>
          <input
            name="name"
            value={newInspectionItem.name}
            onChange={handleNewChange}
            placeholder="Inspection Item Name"
            required
            className="form-control"
          />
        </div>

        <div className="form-group mb-2">
          <label>Category</label>
          <select
            name="categoryId"
            className="form-control"
            value={newInspectionItem.categoryId === 0 ? '' : newInspectionItem.categoryId}
            onChange={handleNewChange}
            required
            disabled={categoriesLoading}
          >
            <option value="" disabled>
              {categoriesLoading ? 'Loading categories…' : 'Select a category'}
            </option>
            {categories.map(c => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group form-check mb-2">
          <input
            id="new-hasPhoto"
            name="hasPhoto"
            type="checkbox"
            className="form-check-input"
            checked={!!newInspectionItem.hasPhoto}
            onChange={handleNewChange}
          />
          <label className="form-check-label" htmlFor="new-hasPhoto">
            Requires Photo
          </label>
        </div>

        <div className="form-group mb-2">
          <label>Help Text</label>
          <textarea
            name="description"
            className="form-control"
            rows={3}
            value={newInspectionItem.description}
            onChange={handleNewChange}
            placeholder="What is being inspected / guidance for the tech"
          />
        </div>
        <div className="form-group mb-2">
          <label>Status Label Green</label>
          <input
            name="statusLabelGreen"
            value={newInspectionItem.statusLabelGreen}
            onChange={handleNewChange}
            placeholder="Status Label Green"
            required
            className="form-control"
          />
        </div>
        <div className="form-group mb-2">
          <label>Status Label Yellow</label>
          <input
            name="statusLabelYellow"
            value={newInspectionItem.statusLabelYellow}
            onChange={handleNewChange}
            placeholder="Status Label Yellow"
            required
            className="form-control"
          />
        </div>
        <div className="form-group mb-2">
          <label>Status Label Red</label>
          <input
            name="statusLabelRed"
            value={newInspectionItem.statusLabelRed}
            onChange={handleNewChange}
            placeholder="Status Label Red"
            required
            className="form-control"
          />
        </div>
        <div className="form-group mb-4">
          <label>Sort Order</label>
          <input
            name="sortOrder"
            value={newInspectionItem.sortOrder}
            onChange={handleNewChange}
            placeholder="Sort Order"
            required
            className="form-control"
            type='number'
          />
        </div>


        <button type="submit" className="btn btn-primary">Add Inspection Item</button>
      </form>
    </div>
  );
}

export default InspectionItems;
